import { FC, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { getAuthListsAsync } from "redux/actions/authList.action";

import { useTranslation } from "react-i18next";
import { useMediaQuery } from "context/media/MediaContext";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { AddAgentValidationSchema } from "validations/agent.validation";

import {
  IAgentAuthListItem,
  IAddNewAgentForm,
} from "models/Employee/employeeManagement.model";
import { AddNewOrUpdateAgentAsync } from "services/employeeManagement.service";

import {
  Col,
  ElementWrapper,
  Row,
  StyledFormWrapper,
} from "components/BaseComponents/Flex";
import {
  Button,
  Panel,
  PanelHeader,
  Spinner,
  TextField,
  Typography,
} from "components";
import { DIGIPOSTHEME } from "theme/digiPosTheme";
import Alert from "components/BaseComponents/Alert/Alert";

import { TokenInclude } from "helpers/GetToken";
import { Mapper } from "helpers/AuthListMapper";
import { eachFirstLetterCapital } from "utils/textfield.utils";
import { AuthListItem } from "../common/AuthListItem";

const AddNewAgent: FC = () => {
  const { t } = useTranslation();
  const { isMobile } = useMediaQuery();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const refScroll = useRef<HTMLHeadElement | null>(null);

  const [isAddSuccess, setIsAddSuccess] = useState<boolean>(false);
  const [isAddChange, setIsAddChange] = useState<boolean>(true);
  const [isAddError, setIsAddError] = useState<boolean>(false);
  const [txt, setTxt] = useState<string>(t("success_add_message").toString());

  const [isAuthCheck, setIsAuthCheck] = useState<boolean>(false);
  const [agentAuthList, setAgentAuthList] = useState<IAgentAuthListItem[]>([]);
  const token = TokenInclude();

  const { authList } = useAppSelector((state) => state.authList);

  const onAuthChange = (isToggle?: boolean): void => {
    if (isToggle) setIsAuthCheck(isToggle);
  };

  const defaultFormValues: IAddNewAgentForm = {
    name: "",
    mobilNo: "",
    mobilNoAgain: "",
  };
  const {
    control,
    handleSubmit,
    errors,
    reset,
    formState: { isSubmitting },
  } = useForm<IAddNewAgentForm>({
    defaultValues: defaultFormValues,
    resolver: yupResolver(AddAgentValidationSchema(t)),
  });

  const formSubmit = async (data: any): Promise<void> => {
    const mobilNo = data.mobilNo;
    const name = eachFirstLetterCapital(data.name);
    delete data.name;
    delete data.mobilNo;

    const authListMapper: IAgentAuthListItem[] = Mapper(data, agentAuthList);

    const retVal = await AddNewOrUpdateAgentAsync(
      token,
      authListMapper,
      mobilNo,
      name,
      false
    );
    if (retVal.Issucceeded) {
      setIsAddSuccess(true);
      setIsAddChange(false);
    } else {
      const errMessage: any = retVal;
      setIsAddError(true);
      setTxt(errMessage);
    }

    if (refScroll?.current) {
      setTimeout(function () {
        refScroll.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }

    reset();
  };

  useEffect(() => {
    if (authList?.AuthList) setAgentAuthList(authList?.AuthList);
    else dispatch(getAuthListsAsync(token));
  }, [authList, token]);

  return (
    <ElementWrapper
      width='872px'
      direction='column'
      justifyContent='flex-start'
      alignItems='flex-start'
      margin='10px auto'
    >
      {(isAddSuccess || isAddError) && (
        <Alert
          alertType={isAddSuccess ? "Success" : "Error"}
          duration={6000}
          isVisible={isAddSuccess || isAddError}
          message={txt}
          setIsAddSuccess={setIsAddSuccess}
          setIsAddError={setIsAddError}
        />
      )}
      <Spinner show={isSubmitting} />
      <section
        ref={refScroll}
        id='addNewAgent-section'
        style={{ position: "absolute", top: 0, scrollBehavior: "smooth" }}
      />
      <Panel width='100%' marginTop='10px'>
        <form onSubmit={handleSubmit(formSubmit)} noValidate>
          <Row backgroundColor={DIGIPOSTHEME.colors.transparent} noMargin>
            <Typography
              margin='10px 0'
              size='21px'
              color={DIGIPOSTHEME.colors.secondaryGraySecondary}
              weight='600'
            >
              {t("add_agent")}
            </Typography>
          </Row>
          <PanelHeader />
          <StyledFormWrapper>
            <Row>
              <Col>
                <TextField
                  control={control}
                  error={errors.name?.message}
                  label={t("just_emp_name").toString()}
                  maxLength={100}
                  name='name'
                  placeholder={t("name_surname_validation").toString()}
                />
              </Col>
            </Row>

            <Row>
              <Col>
                <TextField
                  control={control}
                  error={errors.mobilNo?.message}
                  label={t("phone_number").toString()}
                  maxLength={15}
                  name='mobilNo'
                  type='phoneNumber'
                  placeholder='(5**) *** ** **'
                />
              </Col>
            </Row>

            <Row>
              <Col>
                <TextField
                  control={control}
                  error={errors.mobilNoAgain?.message}
                  label={t("phone_number_again").toString()}
                  maxLength={15}
                  name='mobilNoAgain'
                  type='phoneNumber'
                  placeholder='(5**) *** ** **'
                />
              </Col>
            </Row>
          </StyledFormWrapper>

          <Row backgroundColor={DIGIPOSTHEME.colors.transparent} noMargin>
            <Typography
              margin='10px 0'
              size='21px'
              color={DIGIPOSTHEME.colors.secondaryGraySecondary}
              weight='600'
            >
              {t("agent_add_auth_list")}
            </Typography>
          </Row>
          <PanelHeader />
          <StyledFormWrapper>
            {AuthListItem({
              control,
              agentAuthList,
              isAuthCheck,
              onAuthChange,
              isDetail: false,
            })}
          </StyledFormWrapper>

          <Row
            backgroundColor={DIGIPOSTHEME.colors.secondaryOffWhite}
            justifyContent='space-between'
            isResponsive
            noMargin
            gap
          >
            <Col>
              <Button
                type='button'
                width={isMobile ? "100%" : "100px"}
                text={`← ${t("back")}`}
                onClick={() =>
                  navigate("/employee-panel", {
                    state: { fromAddOrEditAgent: isAddChange },
                  })
                }
                color='light'
              />
            </Col>
            <Col maxWidth='130px'>
              <Button text={t("new_agent").toString()} type='submit' />
            </Col>
          </Row>
        </form>
      </Panel>
    </ElementWrapper>
  );
};

export default AddNewAgent;
